/* eslint-disable max-lines */
import { CheckIcon, PencilSquareIcon, XMarkIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import usePageIndex from "../../hooks/usePageIndex";
import { useSearch } from "../../hooks/useSearch";
import { AffiliateStatusEnum, IAffiliate } from "../../interfaces/endpoints/IAffiliates";
import {
  approveAffiliate,
  deleteAffiliate,
  getAllAffiliates,
  rejectAffiliate
} from "../../services/Affiliates.service";
import Button from "../ui/Button";
import DeleteModal from "../ui/DeleteModal";
import IconBtn from "../ui/IconBtn";
import Textarea from "../ui/input/Textarea";
import Modal from "../ui/Modal";
import Table, { Row } from "../ui/table";
import Pagination from "../ui/table/Pagination";
import Can from "../users/permissions/Can";
import { AffiliateStatusBadge } from "./AffiliateStatusBadge";

const columns = [
  "Emri",
  "Email",
  "Kodi Promo",
  "Statusi",
  "Zbritja % (eSIM / Bus)",
  "Komisioni % (eSIM / Bus)",
  "Data e skadimit",
  "Veprimet"
];

const AffiliatesList = () => {
  const { response, loading, handlePageChange, page, setResponse } = useSearch(
    getAllAffiliates,
    {
      withQuerySearch: true,
      withFilter: true,
      filterSelector: "status"
    }
  );
  const { pageIndex }: any = usePageIndex();

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState<IAffiliate | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  const handleDelete = (id: number) => {
    return deleteAffiliate(id).then((res) => {
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              data: {
                ...prev.data,
                result: prev.data.result.filter(
                  (el: IAffiliate) => el.id !== id
                )
              }
            }
          : null
      );
      return res;
    }) as any;
  };

  const handleApprove = async (affiliate: IAffiliate) => {
    try {
      await approveAffiliate(affiliate.id);
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              data: {
                ...prev.data,
                result: prev.data.result.map((el: IAffiliate) =>
                  el.id === affiliate.id
                    ? { ...el, status: AffiliateStatusEnum.Approved }
                    : el
                )
              }
            }
          : null
      );
      toast.success("Partneri u aprovua me sukses");
    } catch {
      toast.error("Deshtoi aprovimi i partnerit");
    }
  };

  const openRejectModal = (affiliate: IAffiliate) => {
    setSelectedAffiliate(affiliate);
    setRejectNote("");
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!selectedAffiliate) return;
    try {
      await rejectAffiliate(selectedAffiliate.id, rejectNote);
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              data: {
                ...prev.data,
                result: prev.data.result.map((el: IAffiliate) =>
                  el.id === selectedAffiliate.id
                    ? { ...el, status: AffiliateStatusEnum.Rejected }
                    : el
                )
              }
            }
          : null
      );
      toast.success("Partneri u refuzua me sukses");
      setShowRejectModal(false);
    } catch {
      toast.error("Deshtoi refuzimi i partnerit");
    }
  };

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
      <Table
        titles={columns}
        loading={loading}
        className="overflow-x-auto"
        style="hover"
      >
        {(response?.data.result || []).map((row: IAffiliate, i: number) => (
          <Row
            key={i}
            affiliateName={row.affiliateName}
            email={row.email}
            promoCode={row.promoCode || "-"}
            status={<AffiliateStatusBadge status={row.status} />}
            discountPercentage={`${row.esimDiscountPercentage}% / ${row.busTicketDiscountPercentage}%`}
            commissionPercentage={`${row.esimCommissionPercentage}% / ${row.busTicketCommissionPercentage}%`}
            expirationDate={dayjs(row.expirationDate).format("DD-MM-YYYY")}
            actions={
              <span className="flex items-center">
                <Can permissions="affiliates.affiliate.update">
                  {row.status === AffiliateStatusEnum.Pending && (
                    <>
                      <IconBtn
                        className="w-6 h-6 text-green-600"
                        onClick={() => handleApprove(row)}
                      >
                        <CheckIcon className="w-4 h-4" />
                      </IconBtn>
                      <IconBtn
                        className="w-6 h-6 text-red-600"
                        onClick={() => openRejectModal(row)}
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </IconBtn>
                    </>
                  )}
                  <Link
                    to={`/affiliates/edit/${row.id}`}
                    state={{
                      page: pageIndex || page,
                      crumbs: ["Partneret affiliate", "Modifiko partnerin"]
                    }}
                  >
                    <IconBtn className="w-6 h-6">
                      <PencilSquareIcon className="w-4 h-4" />
                    </IconBtn>
                  </Link>
                </Can>
                <Can permissions="affiliates.affiliate.delete">
                  <DeleteModal
                    handleDelete={() => handleDelete(row.id)}
                    params={row.id}
                    successMessage="Partneri u fshi me sukses"
                    errorMessage="Deshtoi fshirja e partnerit"
                  />
                </Can>
              </span>
            }
          />
        ))}
      </Table>
      <Pagination
        onPageChange={handlePageChange}
        totalCount={response?.data.totalCount || 0}
        length={response?.data?.result?.length}
        page={page}
        pageSize={10}
      />
      <Modal
        show={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Refuzo partnerin"
        position="center"
        buttonElement={
          <Button onClick={handleReject}>Konfirmo</Button>
        }
      >
        <div className="p-4 space-y-4">
          {selectedAffiliate && (
            <p className="text-sm text-gray-600">
              Partneri: <strong>{selectedAffiliate.affiliateName}</strong>
            </p>
          )}
          <Textarea
            label="Shenime nga admini"
            value={rejectNote}
            onChange={(e: any) => setRejectNote(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default AffiliatesList;
